/**
 * Authentication context using react-oidc-context with Keycloak.
 *
 * Provides authentication state, user information, and role-based access.
 */

import { createContext, useContext, ReactNode, useCallback, useEffect } from "react";
import { useAuth as useOidcAuth, AuthProvider } from "react-oidc-context";
import { WebStorageStateStore, User as OidcUser } from "oidc-client-ts";
import { setAccessToken } from "../services/api";

// Keycloak configuration - supports both local and production deployments
// For GCP deployment, set VITE_KEYCLOAK_URL environment variable
const KEYCLOAK_URL = import.meta.env.VITE_KEYCLOAK_URL || "http://localhost:8080";
const KEYCLOAK_REALM = import.meta.env.VITE_KEYCLOAK_REALM || "dossier";
const KEYCLOAK_CLIENT_ID = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || "dossier-app";
const SYSTEM_ADMIN_ROLE = "system_admin";

// OIDC configuration for Keycloak
const oidcConfig = {
  authority: `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}`,
  client_id: KEYCLOAK_CLIENT_ID,
  redirect_uri: window.location.origin,
  post_logout_redirect_uri: window.location.origin,
  scope: "openid profile email",
  automaticSilentRenew: true,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  // Handle token refresh before expiry
  accessTokenExpiringNotificationTimeInSeconds: 60,
};

/**
 * User information extracted from Keycloak JWT.
 */
export interface AuthUser {
  id: string;
  email: string;
  username: string;
  firstName?: string;
  lastName?: string;
  displayName: string;
  roles: string[];
  isAdmin: boolean;
}

/**
 * Auth context value.
 */
interface AuthContextValue {
  /** Whether auth is being initialized */
  isLoading: boolean;
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** Current user, null if not authenticated */
  user: AuthUser | null;
  /** Access token for API calls */
  accessToken: string | null;
  /** Whether user has admin role */
  isAdmin: boolean;
  /** Sign in - redirects to Keycloak */
  login: () => void;
  /** Sign out - redirects to Keycloak logout */
  logout: () => void;
  /** Check if user has a specific role */
  hasRole: (role: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Extract user info from OIDC user profile.
 */
function extractUser(oidcUser: OidcUser | null | undefined): AuthUser | null {
  if (!oidcUser?.profile) {
    return null;
  }

  const profile = oidcUser.profile;

  // Extract roles from realm_access claim (Keycloak standard)
  const realmAccess = (profile as Record<string, unknown>).realm_access as
    | { roles?: string[] }
    | undefined;
  const roles = realmAccess?.roles || [];

  const firstName = (profile.given_name as string) || undefined;
  const lastName = (profile.family_name as string) || undefined;
  const email = (profile.email as string) || "";

  return {
    id: profile.sub || "",
    email,
    username: (profile.preferred_username as string) || email,
    firstName,
    lastName,
    displayName:
      firstName && lastName ? `${firstName} ${lastName}` : email,
    roles,
    isAdmin: roles.includes(SYSTEM_ADMIN_ROLE),
  };
}

/**
 * Internal auth provider content that uses OIDC hooks.
 */
function AuthProviderContent({ children }: { children: ReactNode }) {
  const auth = useOidcAuth();

  const user = extractUser(auth.user);
  const accessToken = auth.user?.access_token || null;
  const isAdmin = user?.isAdmin || false;

  // Sync access token with API service
  useEffect(() => {
    setAccessToken(accessToken);
  }, [accessToken]);

  const login = useCallback(() => {
    auth.signinRedirect();
  }, [auth]);

  const logout = useCallback(() => {
    auth.signoutRedirect();
  }, [auth]);

  const hasRole = useCallback(
    (role: string) => {
      return user?.roles.includes(role) || false;
    },
    [user]
  );

  const value: AuthContextValue = {
    isLoading: auth.isLoading,
    isAuthenticated: auth.isAuthenticated,
    user,
    accessToken,
    isAdmin,
    login,
    logout,
    hasRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Auth provider component. Wrap your app with this.
 */
export function DossierAuthProvider({ children }: { children: ReactNode }) {
  const onSigninCallback = () => {
    // Remove OIDC params from URL after login
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  return (
    <AuthProvider {...oidcConfig} onSigninCallback={onSigninCallback}>
      <AuthProviderContent>{children}</AuthProviderContent>
    </AuthProvider>
  );
}

/**
 * Hook to access auth context.
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within a DossierAuthProvider");
  }
  return context;
}
